<template>
  <teleport to="body">
    <div class="edv-backdrop" @click.self="emit('close')">
      <div class="edv-modal" role="dialog" aria-modal="true" aria-label="Experience data visualizer">

        <!-- Header -->
        <header class="edv-header">
          <div class="edv-header__left">
            <h2 class="edv-header__title">{{ data.experience.name }}</h2>
            <span class="edv-header__mode-badge">{{ data.experience.experienceMode }}</span>
            <span
              v-if="headerStatus"
              class="edv-status-badge"
              :class="`edv-status-badge--${headerStatus.toLowerCase()}`"
            >
              {{ headerStatus }}
            </span>
            <span
              v-else-if="activeCampaignIds.size > 0 && campaignStatusMap.size > 0"
              class="edv-status-badge edv-status-badge--mixed"
            >
              Mixed
            </span>
          </div>
          <div class="edv-header__stats">
            <div class="edv-stat">
              <span class="edv-stat__value">{{ filteredTotals.uniqueUsers }}</span>
              <span class="edv-stat__label">respondents</span>
            </div>
            <div class="edv-stat">
              <span class="edv-stat__value">{{ filteredTotals.sessionsCompleted }}</span>
              <span class="edv-stat__label">completed</span>
            </div>
            <div class="edv-stat">
              <span class="edv-stat__value">{{ filteredTotals.answersTotal }}</span>
              <span class="edv-stat__label">answers</span>
            </div>
            <div class="edv-stat">
              <span class="edv-stat__value">{{ filteredTotals.campaignsTouched }}</span>
              <span class="edv-stat__label">campaigns</span>
            </div>
            <div v-if="dateRange" class="edv-stat">
              <span class="edv-stat__value">{{ dateRange }}</span>
              <span class="edv-stat__label">date range</span>
            </div>
          </div>
          <button class="edv-header__close" type="button" aria-label="Close" @click="emit('close')">
            &times;
          </button>
        </header>

        <!-- Campaign filter chip bar (only shown when >1 campaign) -->
        <div v-if="data.campaigns.length > 1" class="edv-chip-bar">
          <div class="edv-chip-bar__controls">
            <button type="button" class="edv-chip-bar__master" @click="selectAllCampaigns">All</button>
            <button type="button" class="edv-chip-bar__master" @click="selectNoCampaigns">None</button>
          </div>
          <div class="edv-chip-bar__chips">
            <button
              v-for="campaign in data.campaigns"
              :key="campaign.campaignId"
              type="button"
              class="edv-chip"
              :class="{ 'edv-chip--active': activeCampaignIds.has(campaign.campaignId) }"
              @click="toggleCampaign(campaign.campaignId)"
            >
              <span
                class="edv-chip__dot"
                :style="{ background: campaignColor(campaign.campaignId) }"
              ></span>
              <span class="edv-chip__name">{{ campaign.campaignName }}</span>
              <span
                v-if="campaignStatusMap.get(campaign.campaignId)"
                class="edv-status-badge edv-status-badge--small"
                :class="`edv-status-badge--${campaignStatusMap.get(campaign.campaignId)!.toLowerCase()}`"
              >
                {{ campaignStatusMap.get(campaign.campaignId) }}
              </span>
              <span class="edv-chip__count">({{ campaign.sessionCount }})</span>
            </button>
          </div>
          <span class="edv-chip-bar__summary">
            Showing {{ activeCampaignIds.size }} of {{ data.campaigns.length }} campaigns
          </span>
        </div>

        <!-- Empty state -->
        <div v-if="data.totals.sessionsTotal === 0" class="edv-empty">
          <p>No respondents yet for this experience.</p>
        </div>

        <!-- Tab bar + content -->
        <div v-else class="edv-body">
          <div class="edv-tabs" role="tablist">
            <button
              v-for="tab in TABS"
              :key="tab.id"
              class="edv-tab"
              :class="{ 'edv-tab--active': activeTab === tab.id }"
              role="tab"
              :aria-selected="activeTab === tab.id"
              type="button"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- By Session tab -->
          <div v-if="activeTab === 'sessions'" class="edv-panel edv-panel--sessions">
            <div v-if="filteredSessions.length === 0" class="edv-empty edv-empty--tab">No sessions for selected campaigns.</div>
            <div
              v-for="session in filteredSessions"
              :key="session.sessionId"
              class="edv-session-card"
            >
              <div class="edv-session-card__top">
                <div class="edv-session-card__identity">
                  <span class="edv-session-card__name">{{ userDisplayName(session) }}</span>
                  <span class="edv-session-card__id">{{ userIdentifier(session) }}</span>
                </div>
                <div class="edv-session-card__meta">
                  <span
                    class="edv-badge"
                    :class="`edv-badge--${session.status}`"
                  >{{ session.status }}</span>
                  <span
                    class="edv-session-card__campaign"
                    :style="data.campaigns.length > 1 ? { color: campaignColor(session.campaignId) } : {}"
                  >
                    <span
                      v-if="data.campaigns.length > 1"
                      class="edv-session-card__campaign-dot"
                      :style="{ background: campaignColor(session.campaignId) }"
                    ></span>
                    {{ session.campaignName }}
                  </span>
                  <span class="edv-session-card__time">{{ formatDate(session.startedAt) }}</span>
                  <span v-if="session.sessionDurationMs" class="edv-session-card__duration">
                    {{ formatDuration(session.sessionDurationMs) }}
                  </span>
                  <span v-if="session.roundId" class="edv-session-card__round">
                    Round: {{ session.roundId }}
                  </span>
                  <span v-if="data.experience.experienceMode === 'quiz'" class="edv-session-card__score">
                    Score: {{ session.score }}
                  </span>
                </div>
                <button
                  type="button"
                  class="edv-session-card__expand-btn"
                  @click="toggleSession(session.sessionId)"
                >
                  {{ expandedSessions.has(session.sessionId) ? 'Hide answers' : `Show answers (${answersForSession(session.sessionId).length})` }}
                </button>
              </div>

              <div v-if="expandedSessions.has(session.sessionId)" class="edv-session-card__answers">
                <div v-if="answersForSession(session.sessionId).length === 0" class="edv-session-card__no-answers">
                  No answers recorded for this session.
                </div>
                <div
                  v-for="answer in answersForSession(session.sessionId)"
                  :key="`${answer.sessionId}-${answer.questionId}-${answer.stageOrder}`"
                  class="edv-answer-row"
                >
                  <span class="edv-answer-row__q">{{ answer.questionText || answer.questionId }}</span>
                  <span class="edv-answer-row__response">{{ formatAnswerResponse(answer) }}</span>
                  <span v-if="answer.durationFromPrevStageMs != null" class="edv-answer-row__time">
                    +{{ formatDuration(answer.durationFromPrevStageMs) }}
                  </span>
                  <span
                    v-if="answer.correct != null"
                    class="edv-badge"
                    :class="answer.correct ? 'edv-badge--completed' : 'edv-badge--abandoned'"
                  >
                    {{ answer.correct ? 'correct' : 'incorrect' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- By Question tab -->
          <div v-if="activeTab === 'questions'" class="edv-panel edv-panel--questions">
            <div v-if="questionGroups.length === 0" class="edv-empty edv-empty--tab">No questions found.</div>
            <div
              v-for="group in questionGroups"
              :key="group.questionId"
              class="edv-question-block"
            >
              <div class="edv-question-block__header">
                <span class="edv-question-block__text">{{ group.questionText || group.questionId }}</span>
                <span v-if="group.questionRound" class="edv-question-block__round">{{ group.questionRound }}</span>
                <span v-if="group.skippedCount > 0" class="edv-question-block__skipped">{{ group.skippedCount }} skipped</span>
              </div>

              <!-- Choice distribution bars (segmented by campaign when multiple active) -->
              <div v-if="group.choiceDistribution.length > 0" class="edv-bars">
                <div
                  v-for="choice in group.choiceDistribution"
                  :key="choice.label"
                  class="edv-bar-row"
                >
                  <span class="edv-bar-row__label">{{ choice.label }}</span>
                  <div class="edv-bar-row__track">
                    <template v-if="data.campaigns.length > 1 && choice.segments.length > 0">
                      <!-- Stacked segments per campaign -->
                      <div class="edv-bar-row__stacked">
                        <span
                          v-for="seg in choice.segments"
                          :key="seg.campaignId"
                          class="edv-bar-row__segment"
                          :style="{
                            width: `${Math.max(2, Math.round(seg.segPct))}px`,
                            background: campaignColor(seg.campaignId),
                          }"
                          :title="`${seg.campaignName}: ${seg.count}`"
                        ></span>
                      </div>
                    </template>
                    <template v-else>
                      <svg
                        class="edv-bar-row__svg"
                        :width="Math.max(2, Math.round(choice.pct))"
                        height="16"
                        role="img"
                        :aria-label="`${choice.pct.toFixed(0)}%`"
                      >
                        <rect x="0" y="0" :width="Math.max(2, Math.round(choice.pct))" height="16" fill="currentColor" rx="2" />
                      </svg>
                    </template>
                    <span class="edv-bar-row__pct">{{ choice.pct.toFixed(0) }}%</span>
                    <span class="edv-bar-row__n">({{ choice.count }})</span>
                  </div>
                </div>
              </div>

              <!-- Rating histogram -->
              <div v-else-if="group.responseType === 'rating' && group.ratingBins.length > 0" class="edv-bars">
                <div v-for="bin in group.ratingBins" :key="bin.value" class="edv-bar-row">
                  <span class="edv-bar-row__label">{{ bin.value }}</span>
                  <div class="edv-bar-row__track">
                    <svg
                      class="edv-bar-row__svg"
                      :width="Math.max(2, Math.round(bin.pct))"
                      height="16"
                      role="img"
                      :aria-label="`${bin.pct.toFixed(0)}%`"
                    >
                      <rect x="0" y="0" :width="Math.max(2, Math.round(bin.pct))" height="16" fill="currentColor" rx="2" />
                    </svg>
                    <span class="edv-bar-row__pct">{{ bin.pct.toFixed(0) }}%</span>
                    <span class="edv-bar-row__n">({{ bin.count }})</span>
                  </div>
                </div>
              </div>

              <!-- Free-text samples -->
              <div v-else-if="group.responseType === 'free-text' && group.textSamples.length > 0" class="edv-text-samples">
                <div v-for="(sample, i) in group.textSamples" :key="i" class="edv-text-samples__item">
                  "{{ sample }}"
                </div>
                <p v-if="group.totalResponses > group.textSamples.length" class="edv-text-samples__more">
                  + {{ group.totalResponses - group.textSamples.length }} more responses
                </p>
              </div>

              <span class="edv-question-block__count">{{ group.totalResponses }} responses</span>
            </div>
          </div>

          <!-- Raw data table tab -->
          <div v-if="activeTab === 'raw'" class="edv-panel edv-panel--raw">
            <div class="edv-raw-controls">
              <input
                v-model="rawFilter"
                class="edv-raw-controls__filter"
                type="text"
                placeholder="Filter by sessionId / userId / questionId…"
              />
              <span class="edv-raw-controls__count">
                {{ filteredAnswers.length }} rows
                <span v-if="filteredAnswers.length > RAW_PAGE_SIZE"> — showing {{ rawPage * RAW_PAGE_SIZE }} of {{ filteredAnswers.length }}</span>
              </span>
            </div>
            <div class="edv-raw-scroll">
              <table class="edv-table">
                <thead>
                  <tr>
                    <th
                      v-for="col in RAW_COLUMNS"
                      :key="col.key"
                      class="edv-table__th"
                      @click="toggleSort(col.key)"
                    >
                      {{ col.label }}
                      <span v-if="sortKey === col.key" class="edv-table__sort-icon">
                        {{ sortDir === 'asc' ? '↑' : '↓' }}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in pagedAnswers"
                    :key="idx"
                    class="edv-table__tr"
                  >
                    <td v-for="col in RAW_COLUMNS" :key="col.key" class="edv-table__td">
                      <template v-if="col.key === 'campaignName' && data.campaigns.length > 1">
                        <span class="edv-table__campaign-dot" :style="{ background: campaignColor(sessionCampaignId(row.sessionId)) }"></span>
                        {{ formatRawCell(row, col.key) }}
                      </template>
                      <template v-else>
                        {{ formatRawCell(row, col.key) }}
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="filteredAnswers.length > rawPage * RAW_PAGE_SIZE" class="edv-raw-more">
              <button type="button" class="edv-raw-more__btn" @click="rawPage++">
                Show more ({{ Math.min(RAW_PAGE_SIZE, filteredAnswers.length - rawPage * RAW_PAGE_SIZE) }} remaining)
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import type { ExperienceExportResponse } from '@/types/experience-export'
import { campaignsService, type ManagedCampaign } from '@/services/campaigns'
import {
  bestRedactedIdentifier,
  redactEmail,
  redactName,
  redactWalletId,
} from '@/utils/pii-redact'
import { campaignColor } from '@/utils/campaign-colors'

const props = defineProps<{
  data: ExperienceExportResponse
}>()

const emit = defineEmits<{
  close: []
}>()

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'sessions', label: 'By session' },
  { id: 'questions', label: 'By question' },
  { id: 'raw', label: 'Raw data' },
] as const

type TabId = (typeof TABS)[number]['id']
const activeTab = ref<TabId>('sessions')

// ─── Campaign filter ───────────────────────────────────────────────────────────

const activeCampaignIds = ref<Set<string>>(
  new Set(props.data.campaigns.map((c) => c.campaignId)),
)

function toggleCampaign(campaignId: string) {
  const next = new Set(activeCampaignIds.value)
  if (next.has(campaignId)) {
    next.delete(campaignId)
  } else {
    next.add(campaignId)
  }
  activeCampaignIds.value = next
  rawPage.value = 1
}

function selectAllCampaigns() {
  activeCampaignIds.value = new Set(props.data.campaigns.map((c) => c.campaignId))
  rawPage.value = 1
}

function selectNoCampaigns() {
  activeCampaignIds.value = new Set()
  rawPage.value = 1
}

// ─── Filtered views ───────────────────────────────────────────────────────────

const filteredSessions = computed(() =>
  props.data.sessions.filter((s) => activeCampaignIds.value.has(s.campaignId)),
)

const filteredAnswers_base = computed(() =>
  props.data.answers.filter((a) => {
    const session = sessionsLookup.value.get(a.sessionId)
    return session ? activeCampaignIds.value.has(session.campaignId) : true
  }),
)

const filteredTotals = computed(() => {
  const sessions = filteredSessions.value
  const answers = filteredAnswers_base.value
  const uniqueUsers = new Set(sessions.map((s) => s.userId)).size
  const sessionsCompleted = sessions.filter((s) => s.status === 'completed').length
  return {
    uniqueUsers,
    sessionsCompleted,
    answersTotal: answers.length,
    campaignsTouched: activeCampaignIds.value.size,
  }
})

// ─── Campaign lifecycle status ────────────────────────────────────────────────

type LifecycleStatus = 'Active' | 'Paused' | 'Scheduled' | 'Ended' | 'Unscheduled'

function deriveLifecycleStatus(campaign: ManagedCampaign): LifecycleStatus {
  if (campaign.paused) return 'Paused'
  const now = Date.now()
  const start = campaign.startDate ? new Date(campaign.startDate).getTime() : null
  const end = campaign.endDate ? new Date(campaign.endDate).getTime() : null
  if (!start && !end) return 'Unscheduled'
  if (start && !Number.isNaN(start) && now < start) return 'Scheduled'
  if (end && !Number.isNaN(end) && now > end) return 'Ended'
  return 'Active'
}

const campaignStatusMap = ref<Map<string, LifecycleStatus>>(new Map())

onMounted(async () => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
  }
  try {
    const all = await campaignsService.listCampaigns(200)
    const map = new Map<string, LifecycleStatus>()
    for (const c of all) map.set(c.id, deriveLifecycleStatus(c))
    campaignStatusMap.value = map
  } catch {
    // non-fatal: just no status badge
  }
})

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
  }
})

const headerStatus = computed<LifecycleStatus | null>(() => {
  const ids = [...activeCampaignIds.value]
  if (ids.length === 0 || campaignStatusMap.value.size === 0) return null
  const statuses = ids.map((id) => campaignStatusMap.value.get(id)).filter(Boolean) as LifecycleStatus[]
  if (statuses.length === 0) return null
  const first = statuses[0]
  return statuses.every((s) => s === first) ? first : null
})

// ─── Header stats ─────────────────────────────────────────────────────────────

const dateRange = computed(() => {
  const sessions = filteredSessions.value
  if (sessions.length === 0) return null
  const dates = sessions.map((s) => s.startedAt).sort()
  const first = new Date(dates[0]!).toLocaleDateString()
  const last = new Date(dates[dates.length - 1]!).toLocaleDateString()
  return first === last ? first : `${first} – ${last}`
})

// ─── Session tab ──────────────────────────────────────────────────────────────

const expandedSessions = ref<Set<string>>(new Set())

function toggleSession(sessionId: string) {
  const next = new Set(expandedSessions.value)
  if (next.has(sessionId)) {
    next.delete(sessionId)
  } else {
    next.add(sessionId)
  }
  expandedSessions.value = next
}

function answersForSession(sessionId: string) {
  return props.data.answers.filter((a) => a.sessionId === sessionId)
}

function userDisplayName(session: ExperienceExportResponse['sessions'][number]): string {
  // PII redaction at display time only — full name is preserved in CSV export.
  return redactName(session.userFirstName, session.userLastName)
}

function userIdentifier(session: ExperienceExportResponse['sessions'][number]): string {
  // Visualizer shows half-redacted identity; CSV download has the full value.
  return bestRedactedIdentifier({
    email: session.userEmail,
    externalId: session.userExternalId,
    walletId: session.walletId,
  })
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000)
  const minutes = Math.floor(totalSec / 60)
  const seconds = totalSec % 60
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

function formatAnswerResponse(answer: ExperienceExportResponse['answers'][number]): string {
  if (answer.selectedChoiceLabel) return answer.selectedChoiceLabel
  if (answer.selectedChoiceIds && answer.selectedChoiceIds.length > 0) return answer.selectedChoiceIds.join(', ')
  if (answer.selectedChoiceId) return answer.selectedChoiceId
  if (answer.textResponse) return answer.textResponse
  if (answer.ratingValue != null) return String(answer.ratingValue)
  if (answer.booleanValue != null) return answer.booleanValue ? 'Yes' : 'No'
  return '—'
}

// ─── Question tab ─────────────────────────────────────────────────────────────

type ChoiceSegment = { campaignId: string; campaignName: string; count: number; segPct: number }
type ChoiceEntry = { label: string; count: number; pct: number; segments: ChoiceSegment[] }
type RatingBin = { value: number; count: number; pct: number }

type QuestionGroup = {
  questionId: string
  questionText: string | null
  questionRound: string | null
  responseType: ExperienceExportResponse['answers'][number]['responseType']
  totalResponses: number
  skippedCount: number
  choiceDistribution: ChoiceEntry[]
  ratingBins: RatingBin[]
  textSamples: string[]
}

const questionGroups = computed<QuestionGroup[]>(() => {
  const activeAnswers = filteredAnswers_base.value
  const groupMap = new Map<string, ExperienceExportResponse['answers']>()
  for (const answer of activeAnswers) {
    const existing = groupMap.get(answer.questionId) ?? []
    existing.push(answer)
    groupMap.set(answer.questionId, existing)
  }

  return Array.from(groupMap.entries()).map(([questionId, answers]) => {
    const first = answers[0]!
    const totalResponses = answers.length
    const skippedCount = answers.filter(
      (a) => a.selectedChoiceId == null && a.selectedChoiceIds == null && a.textResponse == null && a.ratingValue == null && a.booleanValue == null,
    ).length

    const choiceDistribution: ChoiceEntry[] = []
    const ratingBins: RatingBin[] = []
    const textSamples: string[] = []

    const rtype = first.responseType

    if (rtype === 'single-select' || rtype === 'quiz' || rtype === 'boolean') {
      // Per-label totals
      const counts = new Map<string, number>()
      // Per-label per-campaign counts
      const segCounts = new Map<string, Map<string, number>>()

      for (const a of answers) {
        const label = a.selectedChoiceLabel ?? a.selectedChoiceId ?? (a.booleanValue != null ? (a.booleanValue ? 'Yes' : 'No') : null) ?? '(no response)'
        counts.set(label, (counts.get(label) ?? 0) + 1)

        const session = sessionsLookup.value.get(a.sessionId)
        const cid = session?.campaignId ?? ''
        if (cid) {
          const perLabel = segCounts.get(label) ?? new Map<string, number>()
          perLabel.set(cid, (perLabel.get(cid) ?? 0) + 1)
          segCounts.set(label, perLabel)
        }
      }
      for (const [label, count] of counts.entries()) {
        const pct = totalResponses > 0 ? (count / totalResponses) * 100 : 0
        const segments: ChoiceSegment[] = []
        const perLabel = segCounts.get(label)
        if (perLabel) {
          for (const [cid, cnt] of perLabel.entries()) {
            const campaign = props.data.campaigns.find((c) => c.campaignId === cid)
            segments.push({
              campaignId: cid,
              campaignName: campaign?.campaignName ?? cid,
              count: cnt,
              segPct: totalResponses > 0 ? (cnt / totalResponses) * 100 : 0,
            })
          }
        }
        choiceDistribution.push({ label, count, pct, segments })
      }
      choiceDistribution.sort((a, b) => b.count - a.count)
    } else if (rtype === 'multi-select') {
      const counts = new Map<string, number>()
      for (const a of answers) {
        const ids = a.selectedChoiceIds ?? (a.selectedChoiceId ? [a.selectedChoiceId] : [])
        for (const id of ids) {
          counts.set(id, (counts.get(id) ?? 0) + 1)
        }
      }
      for (const [label, count] of counts.entries()) {
        choiceDistribution.push({ label, count, pct: totalResponses > 0 ? (count / totalResponses) * 100 : 0, segments: [] })
      }
      choiceDistribution.sort((a, b) => b.count - a.count)
    } else if (rtype === 'rating') {
      const counts = new Map<number, number>()
      for (const a of answers) {
        if (a.ratingValue != null) counts.set(a.ratingValue, (counts.get(a.ratingValue) ?? 0) + 1)
      }
      const allRatings = Array.from(counts.keys()).sort((a, b) => a - b)
      for (const value of allRatings) {
        const count = counts.get(value) ?? 0
        ratingBins.push({ value, count, pct: totalResponses > 0 ? (count / totalResponses) * 100 : 0 })
      }
    } else if (rtype === 'free-text') {
      for (const a of answers) {
        if (a.textResponse && textSamples.length < 5) textSamples.push(a.textResponse)
      }
    }

    return {
      questionId,
      questionText: first.questionText,
      questionRound: first.questionRound,
      responseType: rtype,
      totalResponses,
      skippedCount,
      choiceDistribution,
      ratingBins,
      textSamples,
    }
  })
})

// ─── Raw data tab ─────────────────────────────────────────────────────────────

const RAW_PAGE_SIZE = 100
const rawPage = ref(1)
const rawFilter = ref('')
const sortKey = ref<string>('recordedAt')
const sortDir = ref<'asc' | 'desc'>('asc')

const sessionsLookup = computed(() => {
  const map = new Map<string, ExperienceExportResponse['sessions'][number]>()
  for (const s of props.data.sessions) map.set(s.sessionId, s)
  return map
})

function sessionCampaignId(sessionId: string): string {
  return sessionsLookup.value.get(sessionId)?.campaignId ?? ''
}

const RAW_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'sessionId', label: 'Session' },
  { key: 'userId', label: 'User' },
  { key: 'userEmail', label: 'Email' },
  { key: 'campaignName', label: 'Campaign' },
  { key: 'questionText', label: 'Question' },
  { key: 'responseType', label: 'Type' },
  { key: 'response', label: 'Response' },
  { key: 'correct', label: 'Correct' },
  { key: 'recordedAt', label: 'Recorded at' },
  { key: 'durationFromStartMs', label: 'From start (ms)' },
]

type FlatAnswer = ExperienceExportResponse['answers'][number] & {
  userId: string | null
  userEmail: string | null
  campaignName: string | null
  response: string
}

const flatAnswers = computed<FlatAnswer[]>(() =>
  filteredAnswers_base.value.map((a) => {
    const session = sessionsLookup.value.get(a.sessionId)
    return {
      ...a,
      userId: session?.userId ?? null,
      userEmail: session?.userEmail ?? null,
      campaignName: session?.campaignName ?? null,
      response: formatAnswerResponse(a),
    }
  }),
)

const filteredAnswers = computed<FlatAnswer[]>(() => {
  const q = rawFilter.value.trim().toLowerCase()
  const sorted = [...flatAnswers.value].sort((a, b) => {
    const av = String((a as Record<string, unknown>)[sortKey.value] ?? '')
    const bv = String((b as Record<string, unknown>)[sortKey.value] ?? '')
    return sortDir.value === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
  })
  if (!q) return sorted
  return sorted.filter(
    (a) =>
      a.sessionId.toLowerCase().includes(q) ||
      (a.userId ?? '').toLowerCase().includes(q) ||
      a.questionId.toLowerCase().includes(q) ||
      (a.userEmail ?? '').toLowerCase().includes(q),
  )
})

const pagedAnswers = computed(() => filteredAnswers.value.slice(0, rawPage.value * RAW_PAGE_SIZE))

function toggleSort(key: string) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
  rawPage.value = 1
}

function formatRawCell(row: FlatAnswer, key: string): string {
  // PII columns are redacted in the visualizer table; CSV download keeps raw.
  if (key === 'userEmail') return redactEmail(row.userEmail)
  if (key === 'userId') return redactWalletId(row.userId)   // walletId-shaped UUID
  const val = (row as Record<string, unknown>)[key]
  if (val === null || val === undefined) return ''
  if (typeof val === 'boolean') return val ? 'true' : 'false'
  if (Array.isArray(val)) return val.join(', ')
  return String(val)
}
</script>

<style scoped>
.edv-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(8, 12, 28, 0.72);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.edv-modal {
  background: var(--color-background);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.22);
  border-radius: 1.2rem;
  width: 90vw;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--color-text);
  box-shadow: 0 1.5rem 3rem rgba(0, 10, 25, 0.45);
}

/* ── Header ───────────────────────────────────────── */

.edv-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  background: rgba(var(--vt-c-accent-rgb), 0.04);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.edv-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.edv-header__title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edv-header__mode-badge {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 10px;
  background: var(--vt-c-accent);
  border: 1px solid var(--vt-c-accent);
  border-radius: 999px;
  color: var(--color-background);
  white-space: nowrap;
}

.edv-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.edv-status-badge--small {
  padding: 1px 7px;
  font-size: 0.62rem;
  letter-spacing: 0.03em;
}

.edv-status-badge--active {
  background: #22c55e;
  border-color: #4ade80;
  color: #062814;
}

.edv-status-badge--paused {
  background: #facc15;
  border-color: #fde047;
  color: #422006;
}

.edv-status-badge--ended {
  background: #ef4444;
  border-color: #f87171;
  color: #ffffff;
}

.edv-status-badge--scheduled {
  background: #38bdf8;
  border-color: #7dd3fc;
  color: #082f49;
}

.edv-status-badge--unscheduled {
  background: #94a3b8;
  border-color: #cbd5e1;
  color: #0f172a;
}

.edv-status-badge--mixed {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
}

.edv-header__stats {
  display: flex;
  gap: 20px;
  flex: 1;
  flex-wrap: wrap;
}

.edv-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 52px;
}

.edv-stat__value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
}

.edv-stat__label {
  font-size: 0.65rem;
  color: rgba(224, 224, 240, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.edv-header__close {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 999px;
  color: var(--vt-c-accent);
  font-size: 1.6rem;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
  transition: background 150ms ease, border-color 150ms ease;
}

.edv-header__close:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.14);
  border-color: rgba(var(--vt-c-accent-rgb), 0.3);
  color: var(--vt-c-accent);
}

/* ── Campaign chip bar ────────────────────────────── */

.edv-chip-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  border-bottom: 1px solid rgba(var(--vt-c-accent-rgb), 0.12);
  background: rgba(var(--vt-c-accent-rgb), 0.02);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.edv-chip-bar__controls {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.edv-chip-bar__master {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 4px;
  color: rgba(224, 224, 240, 0.65);
  cursor: pointer;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 8px;
}

.edv-chip-bar__master:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #e0e0f0;
}

.edv-chip-bar__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.edv-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(224, 224, 240, 0.45);
  cursor: pointer;
  font-size: 0.76rem;
  font-weight: 500;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  white-space: nowrap;
}

.edv-chip--active {
  background: rgba(255, 255, 255, 0.09);
  border-color: rgba(255, 255, 255, 0.28);
  color: #e0e0f0;
}

.edv-chip:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e0e0f0;
}

.edv-chip__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.edv-chip__name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edv-chip__count {
  color: rgba(224, 224, 240, 0.4);
  font-size: 0.7rem;
}

.edv-chip--active .edv-chip__count {
  color: rgba(224, 224, 240, 0.6);
}

.edv-chip-bar__summary {
  font-size: 0.72rem;
  color: rgba(224, 224, 240, 0.38);
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Empty state ──────────────────────────────────── */

.edv-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  color: rgba(224, 224, 240, 0.45);
  padding: 40px;
}

.edv-empty--tab {
  padding: 32px;
}

/* ── Body ─────────────────────────────────────────── */

.edv-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Tabs ─────────────────────────────────────────── */

.edv-tabs {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid rgba(var(--vt-c-accent-rgb), 0.12);
}

.edv-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  min-height: 0;
  min-width: 7.5rem;
  padding: 0.5rem 1rem;
  margin: 0;
  background: var(--vt-c-accent);
  border: 1px solid var(--vt-c-accent);
  border-radius: 999px;
  color: var(--color-background);
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.1;
  cursor: pointer;
  white-space: nowrap;
  appearance: none;
  box-shadow: none;
  opacity: 1;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.edv-tab:hover:not(.edv-tab--active) {
  transform: translateY(-1px);
}

.edv-tab--active {
  box-shadow: 0 0 0 2px var(--color-background), 0 0 0 4px var(--vt-c-accent);
}

/* ── Panel (shared scroll container) ─────────────── */

.edv-panel {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* ── Session cards ────────────────────────────────── */

.edv-session-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  border-radius: 0.9rem;
  margin-bottom: 12px;
  padding: 14px 16px;
}

.edv-session-card__top {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.edv-session-card__top > .edv-session-card__expand-btn {
  margin-left: auto;
}

.edv-session-card__identity {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  min-width: 160px;
}

.edv-session-card__name {
  font-weight: 600;
  font-size: 0.9rem;
}

.edv-session-card__id {
  font-size: 0.75rem;
  color: rgba(224, 224, 240, 0.5);
  font-family: monospace;
}

.edv-session-card__meta {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.8rem;
  color: rgba(224, 224, 240, 0.65);
}

.edv-session-card__campaign {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.edv-session-card__campaign-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.edv-session-card__answers-toggle {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.edv-session-card__expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  min-height: 0;
  min-width: 7.5rem;
  padding: 0.4rem 0.9rem;
  margin: 0;
  background: var(--vt-c-accent);
  border: 1px solid var(--vt-c-accent);
  border-radius: 999px;
  color: var(--color-background);
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.1;
  cursor: pointer;
  white-space: nowrap;
  appearance: none;
  box-shadow: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.edv-session-card__expand-btn:hover {
  opacity: 0.88;
  transform: translateY(-1px);
}

.edv-session-card__answers {
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edv-session-card__no-answers {
  font-size: 0.8rem;
  color: rgba(224, 224, 240, 0.4);
}

.edv-answer-row {
  display: flex;
  gap: 10px;
  align-items: baseline;
  font-size: 0.82rem;
  flex-wrap: wrap;
}

.edv-answer-row__q {
  color: rgba(224, 224, 240, 0.6);
  flex: 1;
  min-width: 120px;
}

.edv-answer-row__response {
  font-weight: 500;
}

.edv-answer-row__time {
  color: rgba(224, 224, 240, 0.4);
  font-size: 0.75rem;
}

/* ── Status badges ────────────────────────────────── */

.edv-badge {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 7px;
  border-radius: 20px;
}

.edv-badge--completed {
  background: rgba(57, 255, 20, 0.12);
  border: 1px solid rgba(57, 255, 20, 0.35);
  color: #7dff5a;
}

.edv-badge--started {
  background: rgba(255, 200, 0, 0.1);
  border: 1px solid rgba(255, 200, 0, 0.3);
  color: #ffd740;
}

.edv-badge--abandoned {
  background: rgba(255, 49, 49, 0.1);
  border: 1px solid rgba(255, 49, 49, 0.3);
  color: #ff6b6b;
}

/* ── Question blocks ──────────────────────────────── */

.edv-question-block {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  border-radius: 0.9rem;
  margin-bottom: 12px;
  padding: 14px 16px;
  display: grid;
  grid-template-columns: minmax(14rem, 1.4fr) minmax(18rem, 3fr) auto;
  align-items: center;
  gap: 1.25rem;
}

.edv-question-block__header {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 0;
}

.edv-question-block .edv-bars {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.edv-question-block .edv-bar-row {
  display: grid;
  grid-template-columns: minmax(6rem, 1fr) minmax(8rem, 2fr);
  align-items: center;
  gap: 0.75rem;
}

.edv-question-block .edv-bar-row__label {
  min-width: 0;
}

.edv-question-block .edv-bar-row__track {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edv-question-block__count {
  margin-left: auto;
  white-space: nowrap;
}

.edv-question-block__text {
  font-weight: 600;
  font-size: 0.92rem;
  flex: 1;
  min-width: 160px;
}

.edv-question-block__round {
  font-size: 0.75rem;
  color: rgba(224, 224, 240, 0.5);
  font-style: italic;
}

.edv-question-block__count {
  font-size: 0.78rem;
  color: rgba(224, 224, 240, 0.6);
}

.edv-question-block__skipped {
  font-size: 0.75rem;
  color: rgba(255, 200, 0, 0.7);
}

/* ── Bar charts (inline SVG) ──────────────────────── */

.edv-bars {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edv-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.82rem;
}

.edv-bar-row__label {
  min-width: 140px;
  color: rgba(224, 224, 240, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edv-bar-row__track {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.edv-bar-row__svg {
  color: #9b6dff;
  display: block;
  min-width: 2px;
  max-width: 100px;
}

.edv-bar-row__stacked {
  display: flex;
  height: 16px;
  border-radius: 2px;
  overflow: hidden;
  gap: 1px;
  align-items: stretch;
}

.edv-bar-row__segment {
  display: inline-block;
  height: 16px;
  min-width: 2px;
  border-radius: 2px;
}

.edv-bar-row__pct {
  font-weight: 600;
  min-width: 36px;
  color: #e0e0f0;
}

.edv-bar-row__n {
  color: rgba(224, 224, 240, 0.45);
  font-size: 0.75rem;
}

/* ── Free-text samples ────────────────────────────── */

.edv-text-samples {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.edv-text-samples__item {
  font-size: 0.83rem;
  color: rgba(224, 224, 240, 0.7);
  font-style: italic;
  padding: 4px 10px;
  border-left: 2px solid rgba(155, 109, 255, 0.4);
}

.edv-text-samples__more {
  font-size: 0.78rem;
  color: rgba(224, 224, 240, 0.4);
  margin: 4px 0 0;
}

/* ── Raw data table ───────────────────────────────── */

.edv-raw-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.edv-raw-controls__filter {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  color: #e0e0f0;
  font-size: 0.84rem;
  padding: 6px 12px;
  width: 320px;
  max-width: 100%;
}

.edv-raw-controls__filter::placeholder {
  color: rgba(224, 224, 240, 0.35);
}

.edv-raw-controls__count {
  font-size: 0.8rem;
  color: rgba(224, 224, 240, 0.5);
}

.edv-raw-scroll {
  overflow-x: auto;
}

.edv-table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 0.8rem;
}

.edv-table__th {
  background: rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  color: rgba(224, 224, 240, 0.7);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.edv-table__th:hover {
  background: rgba(255, 255, 255, 0.09);
  color: #e0e0f0;
}

.edv-table__sort-icon {
  margin-left: 4px;
  color: #b490ff;
}

.edv-table__tr:nth-child(even) {
  background: rgba(255, 255, 255, 0.025);
}

.edv-table__td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: 6px 12px;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(224, 224, 240, 0.85);
}

.edv-table__campaign-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: middle;
  flex-shrink: 0;
}

.edv-raw-more {
  margin-top: 16px;
  text-align: center;
}

.edv-raw-more__btn {
  background: rgba(155, 109, 255, 0.12);
  border: 1px solid rgba(155, 109, 255, 0.3);
  border-radius: 6px;
  color: #b490ff;
  cursor: pointer;
  font-size: 0.84rem;
  padding: 8px 20px;
}

.edv-raw-more__btn:hover {
  background: rgba(155, 109, 255, 0.22);
}
</style>
